import Badge from '@/common/components/Badge'
import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessage } from '@/common/components/ErrorMessage'
import MaskedTextField from '@/common/components/MaskedTextField'
import { customUnitMask, numberMask } from '@/common/constants/masks.constant'
import { CompanyBranchValidationSchema } from '@/features/privateCompanies/schemas/createBranch.schema'
import { BoxesIcon, TrashIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

interface ProductCategoryConsumptionRestrictionProps {
  category: CompanyBranchValidationSchema['product_category_restrictions_attributes'][0]
  categoryIndex: number
  removeCategory: () => void
  disabled?: boolean
}

export const ProductCategoryConsumptionRestriction = ({
  category,
  categoryIndex,
  removeCategory,
  disabled,
}: ProductCategoryConsumptionRestrictionProps) => {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext<CompanyBranchValidationSchema>()

  return (
    <Box className="flex flex-col gap-y-4 p-4">
      <Badge
        Icon={BoxesIcon}
        text={category.display_name || ''}
        onDelete={() => removeCategory()}
        className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse overflow-hidden !p-0 gap-0"
        overwriteClassName
        deleteClassName="bg-black p-2"
        contentClassName="px-3 py-2"
        deleteIconColor="var(--foreground)"
        disabled={disabled}
      />

      <ErrorMessage
        name={`product_category_restrictions_attributes.${categoryIndex}`}
        message={errors?.product_category_restrictions_attributes?.[categoryIndex]?.root?.message || ''}
      />

      <MaskedTextField
        className="grow"
        mask={customUnitMask('Km', 999999, false)}
        label="Intervalo Mínimo de Quilometragem entre consumos"
        {...register(
          `product_category_restrictions_attributes.${categoryIndex}.minimum_kilometers_usage_between_consumptions`,
        )}
        errorMessage={
          errors?.product_category_restrictions_attributes?.[categoryIndex]
            ?.minimum_kilometers_usage_between_consumptions?.message
        }
        disabled={disabled}
      />

      <MaskedTextField
        className="grow"
        mask={customUnitMask('Km', 999999, false)}
        label="Intervalo Máximo de Quilometragem entre consumos"
        {...register(
          `product_category_restrictions_attributes.${categoryIndex}.maximum_kilometers_usage_between_consumptions`,
        )}
        errorMessage={
          errors?.product_category_restrictions_attributes?.[categoryIndex]
            ?.maximum_kilometers_usage_between_consumptions?.message
        }
        disabled={disabled}
      />

      <div>
        <p className="text-xs">Intervalo mínimo de Hora-Uso entre consumos</p>

        <ErrorMessage
          name={`product_category_restrictions_attributes.${categoryIndex}.minimum_seconds_usage_between_consumptions`}
          message={
            errors?.product_category_restrictions_attributes?.[categoryIndex]
              ?.minimum_seconds_usage_between_consumptions?.root?.message || ''
          }
        />
      </div>

      <div className="flex gap-x-2">
        <MaskedTextField
          className="grow"
          mask={numberMask(30)}
          label="Dias"
          {...register(
            `product_category_restrictions_attributes.${categoryIndex}.minimum_seconds_usage_between_consumptions.days`,
          )}
          errorMessage={
            errors?.product_category_restrictions_attributes?.[categoryIndex]
              ?.minimum_seconds_usage_between_consumptions?.days?.message
          }
          disabled={disabled}
        />

        <MaskedTextField
          className="grow"
          mask={numberMask(23)}
          label="Horas"
          {...register(
            `product_category_restrictions_attributes.${categoryIndex}.minimum_seconds_usage_between_consumptions.hours`,
          )}
          errorMessage={
            errors?.product_category_restrictions_attributes?.[categoryIndex]
              ?.minimum_seconds_usage_between_consumptions?.hours?.message
          }
          disabled={disabled}
        />

        <MaskedTextField
          className="grow"
          mask={numberMask(59)}
          label="Minutos"
          {...register(
            `product_category_restrictions_attributes.${categoryIndex}.minimum_seconds_usage_between_consumptions.minutes`,
          )}
          errorMessage={
            errors?.product_category_restrictions_attributes?.[categoryIndex]
              ?.minimum_seconds_usage_between_consumptions?.minutes?.message
          }
          disabled={disabled}
        />

        <MaskedTextField
          className="grow"
          mask={numberMask(59)}
          label="Segundos"
          {...register(
            `product_category_restrictions_attributes.${categoryIndex}.minimum_seconds_usage_between_consumptions.seconds`,
          )}
          errorMessage={
            errors?.product_category_restrictions_attributes?.[categoryIndex]
              ?.minimum_seconds_usage_between_consumptions?.seconds?.message
          }
          disabled={disabled}
        />
      </div>

      <div className="flex justify-between">
        <p className="text-xs font-light italic">Limite mínimo de tempo: 1 minuto</p>

        <Button
          variant="neutral"
          className="text-white-800"
          onClick={() => {
            setValue(
              `product_category_restrictions_attributes.${categoryIndex}.minimum_seconds_usage_between_consumptions.days`,
              undefined,
            )
            setValue(
              `product_category_restrictions_attributes.${categoryIndex}.minimum_seconds_usage_between_consumptions.hours`,
              undefined,
            )
            setValue(
              `product_category_restrictions_attributes.${categoryIndex}.minimum_seconds_usage_between_consumptions.minutes`,
              undefined,
            )
            setValue(
              `product_category_restrictions_attributes.${categoryIndex}.minimum_seconds_usage_between_consumptions.seconds`,
              undefined,
            )
          }}
        >
          <TrashIcon strokeWidth={1} className="size-4" />

          <p>Limpar campos</p>
        </Button>
      </div>

      <div>
        <p className="text-xs">Intervalo máximo de Hora-Uso entre consumos</p>

        <ErrorMessage
          name={`product_category_restrictions_attributes.${categoryIndex}.maximum_seconds_usage_between_consumptions`}
          message={
            errors?.product_category_restrictions_attributes?.[categoryIndex]
              ?.maximum_seconds_usage_between_consumptions?.root?.message || ''
          }
        />
      </div>

      <div className="flex gap-x-2">
        <MaskedTextField
          className="grow"
          mask={numberMask(30)}
          label="Dias"
          {...register(
            `product_category_restrictions_attributes.${categoryIndex}.maximum_seconds_usage_between_consumptions.days`,
          )}
          errorMessage={
            errors?.product_category_restrictions_attributes?.[categoryIndex]
              ?.maximum_seconds_usage_between_consumptions?.days?.message
          }
          disabled={disabled}
        />

        <MaskedTextField
          className="grow"
          mask={numberMask(23)}
          label="Horas"
          {...register(
            `product_category_restrictions_attributes.${categoryIndex}.maximum_seconds_usage_between_consumptions.hours`,
          )}
          errorMessage={
            errors?.product_category_restrictions_attributes?.[categoryIndex]
              ?.maximum_seconds_usage_between_consumptions?.hours?.message
          }
          disabled={disabled}
        />

        <MaskedTextField
          className="grow"
          mask={numberMask(59)}
          label="Minutos"
          {...register(
            `product_category_restrictions_attributes.${categoryIndex}.maximum_seconds_usage_between_consumptions.minutes`,
          )}
          errorMessage={
            errors?.product_category_restrictions_attributes?.[categoryIndex]
              ?.maximum_seconds_usage_between_consumptions?.minutes?.message
          }
          disabled={disabled}
        />

        <MaskedTextField
          className="grow"
          mask={numberMask(59)}
          label="Segundos"
          {...register(
            `product_category_restrictions_attributes.${categoryIndex}.maximum_seconds_usage_between_consumptions.seconds`,
          )}
          errorMessage={
            errors?.product_category_restrictions_attributes?.[categoryIndex]
              ?.maximum_seconds_usage_between_consumptions?.seconds?.message
          }
          disabled={disabled}
        />
      </div>

      <div className="flex justify-between">
        <p className="text-xs font-light italic">Limite mínimo de tempo: 1 minuto</p>

        <Button
          variant="neutral"
          className="text-white-800"
          onClick={() => {
            setValue(
              `product_category_restrictions_attributes.${categoryIndex}.maximum_seconds_usage_between_consumptions.days`,
              undefined,
            )
            setValue(
              `product_category_restrictions_attributes.${categoryIndex}.maximum_seconds_usage_between_consumptions.hours`,
              undefined,
            )
            setValue(
              `product_category_restrictions_attributes.${categoryIndex}.maximum_seconds_usage_between_consumptions.minutes`,
              undefined,
            )
            setValue(
              `product_category_restrictions_attributes.${categoryIndex}.maximum_seconds_usage_between_consumptions.seconds`,
              undefined,
            )
          }}
        >
          <TrashIcon strokeWidth={1} className="size-4" />

          <p>Limpar campos</p>
        </Button>
      </div>
    </Box>
  )
}
